import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import { usersAPI } from '../services/api';
import {
  Users as UsersIcon,
  Eye,
  Trash2,
  Search,
  UserCheck,
  UserX,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Globe
} from 'lucide-react';
import { getHijriDate, getGregorianDate } from '../utils/dateUtils';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('فشل في تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = (user) => {
    setSelectedUser(user);
    setViewModalOpen(true);
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await usersAPI.update(userId, { isActive: !currentStatus });
      toast.success(currentStatus ? 'تم تعطيل المستخدم' : 'تم تفعيل المستخدم');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('فشل في تحديث حالة المستخدم');
    }
  };

  const toggleUserRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    try {
      await usersAPI.update(userId, { role: newRole });
      toast.success(newRole === 'admin' ? 'تم ترقية المستخدم إلى مدير' : 'تم تغيير المستخدم إلى مستخدم عادي');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      const errorMsg = error.response?.data?.message || 'فشل في تحديث دور المستخدم';
      toast.error(errorMsg);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;

    try {
      await usersAPI.delete(userId);
      toast.success('تم حذف المستخدم بنجاح');
      fetchUsers();
      setViewModalOpen(false);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('فشل في حذف المستخدم');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && user.isActive) ||
      (filterStatus === 'inactive' && !user.isActive);

    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.isActive).length,
    inactive: users.filter(u => !u.isActive).length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">إدارة المستخدمين</h1>
            <p className="text-text-secondary">عرض وإدارة مستخدمي التطبيق</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">إجمالي المستخدمين</p>
                <p className="text-2xl font-bold text-text-primary mt-2">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <UsersIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">نشطين</p>
                <p className="text-2xl font-bold text-green-600 mt-2">{stats.active}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">غير نشطين</p>
                <p className="text-2xl font-bold text-red-600 mt-2">{stats.inactive}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <UserX className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-divider p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted h-5 w-5" />
              <input
                type="text"
                placeholder="البحث بالاسم أو البريد أو الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 px-4 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="all">جميع المستخدمين</option>
              <option value="active">نشطين</option>
              <option value="inactive">غير نشطين</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary-light">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    المستخدم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    البريد الإلكتروني
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    رقم الهاتف
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    الدور
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    تاريخ التسجيل
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    إجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-primary-light">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt={user.name}
                            className="h-10 w-10 rounded-full object-cover ml-3"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-accent-gold/20 flex items-center justify-center ml-3">
                            <span className="text-accent-gold font-medium">
                              {user.name?.charAt(0) || 'U'}
                            </span>
                          </div>
                        )}
                        <div className="text-sm font-medium text-text-primary">
                          {user.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {user.phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleUserRole(user.id, user.role)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {user.role === 'admin' ? 'مدير' : 'مستخدم'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-text-muted">
                        {getHijriDate(user.createdAt)}
                      </div>
                      <div className="text-xs text-text-muted">
                        {getGregorianDate(user.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewUserDetails(user)}
                      >
                        <Eye className="w-4 h-4 ml-1" />
                        عرض
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <UsersIcon className="mx-auto h-12 w-12 text-text-muted mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">لا توجد مستخدمين</h3>
              <p className="text-text-muted">لا توجد مستخدمين مطابقين لمعايير البحث</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="تفاصيل المستخدم"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center space-x-4 space-x-reverse">
              {selectedUser.profileImage ? (
                <img
                  src={selectedUser.profileImage}
                  alt={selectedUser.name}
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-accent-gold/20 flex items-center justify-center">
                  <span className="text-accent-gold font-bold text-2xl">
                    {selectedUser.name?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-text-primary">{selectedUser.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedUser.role === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedUser.role === 'admin' ? 'مدير' : 'مستخدم'}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedUser.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedUser.isActive ? 'نشط' : 'غير نشط'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-primary-light rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Mail className="w-5 h-5 text-accent-gold ml-2" />
                  <p className="text-sm text-text-muted">البريد الإلكتروني</p>
                </div>
                <p className="text-text-primary font-medium">{selectedUser.email}</p>
              </div>

              <div className="bg-primary-light rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Phone className="w-5 h-5 text-accent-gold ml-2" />
                  <p className="text-sm text-text-muted">رقم الهاتف</p>
                </div>
                <p className="text-text-primary font-medium">{selectedUser.phone || 'غير محدد'}</p>
              </div>

              <div className="bg-primary-light rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Globe className="w-5 h-5 text-accent-gold ml-2" />
                  <p className="text-sm text-text-muted">اللغة المفضلة</p>
                </div>
                <p className="text-text-primary font-medium">
                  {selectedUser.preferredLanguage === 'ar' ? 'العربية' : 'English'}
                </p>
              </div>

              <div className="bg-primary-light rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Calendar className="w-5 h-5 text-accent-gold ml-2" />
                  <p className="text-sm text-text-muted">تاريخ التسجيل</p>
                </div>
                <div className="text-text-primary font-medium space-y-1">
                  <p className="text-xs">هجري: {getHijriDate(selectedUser.createdAt)}</p>
                  <p className="text-xs">ميلادي: {getGregorianDate(selectedUser.createdAt)}</p>
                </div>
              </div>

              {selectedUser.latitude && selectedUser.longitude && (
                <div className="bg-primary-light rounded-lg p-4 md:col-span-2">
                  <div className="flex items-center mb-2">
                    <MapPin className="w-5 h-5 text-accent-gold ml-2" />
                    <p className="text-sm text-text-muted">الموقع</p>
                  </div>
                  <p className="text-text-primary font-medium">
                    {selectedUser.latitude}, {selectedUser.longitude}
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3 space-x-reverse justify-end pt-4 border-t border-divider">
              <Button
                variant="danger"
                onClick={() => deleteUser(selectedUser.id)}
              >
                <Trash2 className="w-4 h-4 ml-1" />
                حذف المستخدم
              </Button>
              <Button
                variant={selectedUser.isActive ? 'secondary' : 'primary'}
                onClick={() => toggleUserStatus(selectedUser.id, selectedUser.isActive)}
              >
                {selectedUser.isActive ? (
                  <>
                    <UserX className="w-4 h-4 ml-1" />
                    تعطيل الحساب
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 ml-1" />
                    تفعيل الحساب
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default Users;
